<template>
  <b-modal
      v-model="visible"
      size="lg"
      centered
      hide-header
      hide-footer
      modal-class="alm-modal"
      body-class="alm-modal__body"
  >
    <div class="alm">
      <!-- Hero header -->
      <div class="alm__hero">
        <div class="alm__hero-icon"><i class="fa-solid fa-people-arrows"></i></div>
        <div class="alm__hero-text">
          <h4 class="alm__hero-title">{{ title || $t('salesDashboard.assignments.title') }}</h4>
          <span class="alm__hero-sub">{{ $t('salesDashboard.assignments.title') }}<template v-if="total"> · {{ total }}</template></span>
        </div>
        <!-- Whole-set source summary (promoted / manual / …) right in the header -->
        <div v-if="sourceEntries.length" class="alm__hero-sources">
          <span v-for="s in sourceEntries" :key="s.source" class="alm-chip" :class="sourceClass(s.source)">
            {{ sourceLabel(s.source) }}: <strong>{{ s.count }}</strong>
          </span>
        </div>
        <button type="button" class="alm__close" @click="visible = false" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <b-overlay :show="loading" rounded opacity="0.4" spinner-variant="primary">
        <div class="alm__table-wrap">
          <table class="alm-table">
            <thead>
            <tr>
              <th>{{ $t('salesDashboard.assignments.time') }}</th>
              <th>{{ $t('salesDashboard.assignments.client') }}</th>
              <th>{{ $t('salesDashboard.assignments.receivedBy') }}</th>
              <th>{{ $t('salesDashboard.assignments.from') }}</th>
              <th>{{ $t('salesDashboard.assignments.source') }}</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(row, i) in rows" :key="row.id || i">
              <td class="text-nowrap">{{ row.time | momentDateTime }}</td>
              <td>
                <span class="alm-table__client" role="button" @click="openClient(row)">
                  {{ row.clientName || (row.clientId ? '#' + row.clientId : '—') }}
                </span>
              </td>
              <td><span class="alm-table__name">{{ row.userName || '—' }}</span></td>
              <td><span :class="{ 'text-muted': !row.previousUserName }">{{ row.previousUserName || '—' }}</span></td>
              <td>
                <span class="alm-chip" :class="sourceClass(row.source)">{{ sourceLabel(row.source) }}</span>
              </td>
            </tr>
            <tr v-if="!loading && !rows.length">
              <td colspan="5" class="alm-table__empty">{{ $t('salesDashboard.noData') }}</td>
            </tr>
            </tbody>
          </table>
        </div>

        <div class="alm__footer" v-if="total > size">
          <b-pagination v-model="page" :total-rows="total" :per-page="size"
                        size="sm" class="mb-0" @change="onPageChange"/>
          <span class="alm__total text-muted">{{ total }}</span>
        </div>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';

const SOURCE_CLASS = {
  CREATE: 'alm-chip--create',
  MANUAL: 'alm-chip--manual',
  RANDOM: 'alm-chip--random',
  PROMOTE: 'alm-chip--promote',
  DEMOTE: 'alm-chip--demote',
};

/**
 * Paginated list of lead/client assignments behind the "leads received"
 * numbers on the Sales Dashboard. Backend: POST /dashboard/assignments/list.
 * Defensive: if the endpoint is missing (backend not restarted yet) it shows
 * the standard error notification and an empty table instead of crashing.
 */
export default {
  name: 'AssignmentListModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    loading: false,
    title: '',
    params: {},
    rows: [],
    total: 0,
    page: 1,
    size: 25,
    // source → count over the WHOLE filtered set (header summary chips)
    sourceCounts: {},
  }),
  computed: {
    sourceEntries() {
      const order = ['PROMOTE', 'MANUAL', 'CREATE', 'RANDOM', 'DEMOTE'];
      const entries = Object.entries(this.sourceCounts || {}).map(([source, count]) => ({ source, count }));
      entries.sort((a, b) => {
        const ia = order.indexOf(a.source);
        const ib = order.indexOf(b.source);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      });
      return entries;
    },
  },
  methods: {
    ...mapActions('dashboard', ['getAssignments']),
    /**
     * @param {String} title modal title (e.g. "John — Leads received")
     * @param {Object} p { userIds, range:{from,until}, orgIds, buIds, kind: 'SALES'|'RETENTION'|null }
     */
    show(title, p) {
      this.title = title || '';
      this.params = p || {};
      this.rows = [];
      this.total = 0;
      this.sourceCounts = {};
      this.page = 1;
      this.visible = true;
      this.fetch();
    },
    fetch() {
      const p = this.params || {};
      this.loading = true;
      this.getAssignments({
        filter: {
          range: p.range || {},
          orgIds: p.orgIds || [],
          buIds: p.buIds || [],
          userIds: p.userIds || [],
          page: this.page,
          size: this.size,
        },
        kind: p.kind || null,
      })
          .then(res => {
            this.rows = Array.isArray(res.data) ? res.data : [];
            const meta = res.pagination || {};
            this.total = meta.total != null ? meta.total : this.rows.length;
            this.sourceCounts = meta.sourceCounts || {};
          })
          .catch(err => {
            // Backend may not expose the endpoint yet (404) — notify, keep the
            // modal usable with an empty list.
            this.rows = [];
            this.total = 0;
            this.showServerErrorNotification('fetchDataError', 'salesDashboard', err);
          })
          .finally(() => {
            this.loading = false;
          });
    },
    onPageChange(p) {
      this.page = p;
      this.$nextTick(() => this.fetch());
    },
    sourceLabel(source) {
      if (!source) return '—';
      const key = `salesDashboard.assignments.sources.${source}`;
      return this.$te(key) ? this.$t(key) : source;
    },
    sourceClass(source) {
      return SOURCE_CLASS[source] || 'alm-chip--other';
    },
    openClient(row) {
      if (!row || !row.clientId) return;
      const route = this.$router.resolve({ name: 'client', params: { clientId: row.clientId } });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss">
.alm-modal {
  .modal-content { border: none; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 60px -20px rgba(20, 24, 50, 0.45); }
  .modal-body.alm-modal__body { padding: 0; }
}

.alm {
  display: flex;
  flex-direction: column;

  &__hero {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    color: #fff;
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }
  &__hero-icon {
    display: flex; align-items: center; justify-content: center;
    width: 48px; height: 48px; border-radius: 13px; font-size: 1.25rem;
    background: rgba(255, 255, 255, 0.22); flex-shrink: 0;
  }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-title { margin: 0; color: #fff; font-weight: 700; font-size: 1.15rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__hero-sub { font-size: 0.78rem; opacity: 0.9; text-transform: uppercase; letter-spacing: 0.04em; }
  &__close {
    border: none; background: rgba(255, 255, 255, 0.18); color: #fff;
    width: 34px; height: 34px; border-radius: 10px; font-size: 1rem; cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease; flex-shrink: 0;
    &:hover { background: rgba(255, 255, 255, 0.32); transform: rotate(90deg); }
  }

  &__table-wrap { max-height: 60vh; overflow: auto; }

  &__footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 1.25rem; border-top: 1px solid rgba(110, 117, 159, 0.12);
  }
  &__total { font-size: 0.8rem; }
}

.alm-table {
  width: 100%;
  font-size: 0.85rem;
  border-collapse: collapse;

  thead th {
    position: sticky; top: 0; z-index: 1;
    background: #f7f9fc; color: #6e6b7b; text-transform: uppercase;
    font-size: 0.7rem; letter-spacing: 0.04em; font-weight: 700;
    padding: 0.65rem 1rem; text-align: left; white-space: nowrap;
  }
  tbody td {
    padding: 0.6rem 1rem;
    border-top: 1px solid rgba(110, 117, 159, 0.1);
    vertical-align: middle;
  }
  tbody tr { transition: background 0.15s ease; }
  tbody tr:hover { background: rgba(0, 123, 255, 0.05); }

  &__client {
    font-weight: 600;
    cursor: pointer;
    &:hover { color: #007BFF; text-decoration: underline; }
  }
  &__name { font-weight: 600; }
  &__empty { text-align: center; color: #b9b9c3; padding: 2rem 1rem !important; }
}

/* Whole-set source summary — sits in the blue hero header, so the chips get
   a near-white pill behind their source color to stay readable */
.alm__hero-sources {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.35rem;
  margin-left: auto;
  margin-right: 0.75rem;

  .alm-chip {
    font-size: 0.72rem;
    padding: 0.22rem 0.65rem;
    background: rgba(255, 255, 255, 0.92);
    strong { margin-left: 0.3rem; }
  }
}

/* Small colored source chips: CREATE / MANUAL / RANDOM / PROMOTE / DEMOTE */
.alm-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.14rem 0.6rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;

  &--create { background: rgba(16, 185, 129, 0.14); color: #059669; }
  &--manual { background: rgba(0, 123, 255, 0.14); color: #007BFF; }
  &--random { background: rgba(245, 158, 11, 0.16); color: #d97706; }
  &--promote { background: rgba(124, 58, 237, 0.14); color: #7c3aed; }
  &--demote { background: rgba(239, 68, 68, 0.14); color: #dc2626; }
  &--other { background: rgba(110, 117, 159, 0.14); color: #6e6b7b; }
}

/* Dark mode */
body.dark-layout {
  .alm-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .alm__footer { border-top-color: rgba(255, 255, 255, 0.08); }
  .alm-table {
    thead th { background: var(--crm-dark-surface-2, #2e3850); color: #8b93ab; }
    tbody td { border-top-color: rgba(255, 255, 255, 0.06); }
    tbody tr:hover { background: rgba(123, 175, 255, 0.08); }
    &__client:hover { color: #8fc1ff; }
    &__empty { color: #7c869c; }
  }
  .alm-chip {
    &--create { background: rgba(46, 213, 162, 0.18); color: #6bdcb1; }
    &--manual { background: rgba(77, 163, 255, 0.18); color: #8fc1ff; }
    &--random { background: rgba(251, 191, 36, 0.18); color: #fbbf24; }
    &--promote { background: rgba(167, 139, 250, 0.2); color: #c4b5fd; }
    &--demote { background: rgba(251, 113, 133, 0.18); color: #fda4af; }
    &--other { background: rgba(255, 255, 255, 0.1); color: #8b93ab; }
  }
}
</style>
